import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL, API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { normalizeEmbedWidget } from '../utils/embedConfig';
import BrandEmbedWidgetBuilder from '../components/admin/BrandEmbedWidgetBuilder';
import BrandNt8WebhookTokens from '../components/admin/BrandNt8WebhookTokens';
import BrandInvitesPanel from '../components/admin/BrandInvitesPanel';
import BrandFeatureLocks from '../components/admin/BrandFeatureLocks';
import GlobalSettingsPanel from '../components/admin/GlobalSettingsPanel';
import CompaniesPanel from '../components/admin/CompaniesPanel';
import BrandThemeEditor from '../components/admin/BrandThemeEditor';
import { getActiveEntitySlug, setActiveEntityBrand } from '../utils/activeEntity';
import { FEATURE_ACCESS_CHANGED_EVENT } from '../hooks/useFeatureAccess';
import './AdminEntities.css';

/**
 * Admin → Brands. Full-admin only (the API enforces it; this page only hides
 * the UI). Manage the brands RiskLo hosts: rename, logo, Whop wiring, member
 * grants, and copying a strategy from one brand into another.
 */

function Stat({ label, value, sub, tone }) {
  return (
    <div className="ae-stat">
      <span className={`ae-stat-value${tone ? ` ae-stat-value--${tone}` : ''}`}>{value}</span>
      <span className="ae-stat-label">{label}</span>
      {sub ? <span className="ae-stat-sub">{sub}</span> : null}
    </div>
  );
}

const MAX_LOGO_BYTES = 512 * 1024;

const isUploadedLogo = (url) => typeof url === 'string' && /^data:image\//i.test(url);

function readPngLogoAsDataUrl(file) {
  if (!file) return Promise.reject(new Error('No file selected.'));
  if (file.type !== 'image/png') {
    return Promise.reject(new Error('Logo must be a PNG file.'));
  }
  if (file.size > MAX_LOGO_BYTES) {
    return Promise.reject(new Error('Logo must be 512 KB or smaller.'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!/^data:image\/png;base64,/i.test(result)) {
        reject(new Error('Logo must be a PNG file.'));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(new Error('Could not read logo file.'));
    reader.readAsDataURL(file);
  });
}

/** Browser tab icon: any image, shrunk to a 64×64 PNG (small enough to store). */
function readImageAsFavicon(file) {
  if (!file) return Promise.reject(new Error('No file selected.'));
  if (!/^image\//.test(file.type)) return Promise.reject(new Error('The icon must be an image.'));
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 64;
        canvas.height = 64;
        const ctx = canvas.getContext('2d');
        const scale = Math.min(64 / img.width, 64 / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (64 - w) / 2, (64 - h) / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(new Error('Could not read that image.'));
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image.')); };
    img.src = url;
  });
}

/** Company plans: what each unlocks (server: utils/entityPlans.js). */
const PLAN_OPTIONS = [
  {
    value: 'starter',
    label: 'Starter · $149/mo',
    unlocks: 'RiskLo-branded app. The company shows as the workspace: its logo, name and accent color.',
  },
  {
    value: 'brand',
    label: 'Brand · $499/mo',
    unlocks: 'Starter, plus the full color theme and layout style. RiskLo stays in the header (“Vector on RiskLo”); emails come from “Vector via RiskLo”.',
  },
  {
    value: 'partner',
    label: 'Partner · $999/mo',
    unlocks: 'Brand, plus brand-first: the company’s logo and name lead the app (“Vector Overview”, browser tab), its own link (risklo.io/vector), upgrade buttons go to its own checkout, emails come from the company, and the AI is “Vector AI”. RiskLo shows as “Powered by RiskLo”. Priced per company.',
  },
  {
    value: 'whitelabel',
    label: 'White-label · $1,500/mo',
    unlocks: 'Partner, plus its own app name, tagline, browser-tab icon, email sender and its own domain. RiskLo only shows as “Powered by RiskLo”.',
  },
];
const PLAN_RANK = { starter: 0, brand: 1, partner: 2, whitelabel: 3 };

/** Legacy deal (Admin → Brands): extras a brand keeps whatever its plan, plus a private note. */
function LegacyDeal({ entity, busy, onSave }) {
  const [keepLook, setKeepLook] = React.useState((entity.planExtras || []).includes('theme'));
  const [note, setNote] = React.useState(entity.planNote || '');
  const dirty = keepLook !== (entity.planExtras || []).includes('theme') || note !== (entity.planNote || '');
  return (
    <div className="ae-legacy">
      <strong>Legacy deal</strong>
      <small className="ae-field-note">For brands on an older or custom price. Admins only; the brand never sees the note.</small>
      <label className="ae-legacy-check">
        <input type="checkbox" checked={keepLook} onChange={(ev) => setKeepLook(ev.target.checked)} />
        Keep colors &amp; layout style whatever the plan
      </label>
      <label>
        <span>Deal note</span>
        <input value={note} maxLength={500} placeholder="e.g. Legacy $50/mo; colors & layout grandfathered" onChange={(ev) => setNote(ev.target.value)} />
      </label>
      <button type="button" className="ae-btn" disabled={busy || !dirty} onClick={() => onSave(keepLook ? ['theme', 'layout'] : [], note)}>Save legacy deal</button>
    </div>
  );
}

/** Identity fields (Admin → Brands): what the brand's users see instead of "RiskLo". */
const IDENTITY_FIELDS = [
  ['appName', 'App name', 'Shown instead of “RiskLo”: sidebar, sign-in page, browser tab, emails.'],
  ['tagline', 'Tagline', 'Under the app name, e.g. “Futures risk dashboard”. Blank = none.'],
  ['legalName', 'Legal name', 'Footer © line. Blank = app name.'],
  ['supportEmail', 'Support email', 'Where clients can reach the company.'],
  ['emailFromName', 'Email sender name', 'Who alert emails come from, e.g. “GemAlgo Alerts”. Blank = app name.'],
];

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');
const fmtNum = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString() : '0');
/** Rates are null when the base is zero — show a dash, never "NaN%" or a fake 0%. */
const fmtPct = (p) => (p == null ? '—' : `${p}%`);

function mergeWhopStatsIntoEntity(entity, whopStats) {
  if (!whopStats || whopStats.pending) {
    return {
      ...entity,
      stats: {
        ...entity.stats,
        members: {
          ...entity.stats?.members,
          ...(whopStats?.members || {}),
          granted: entity.stats?.members?.granted,
          whopPending: Boolean(whopStats?.pending),
        },
      },
    };
  }
  return {
    ...entity,
    stats: {
      ...entity.stats,
      members: {
        ...whopStats.members,
        granted: entity.stats?.members?.granted,
        fromCache: Boolean(whopStats.fromCache),
      },
      signups: whopStats.signups,
      community: whopStats.community,
      riskLoProduct: whopStats.riskLoProduct,
      whopFetchedAt: whopStats.fetchedAt || null,
      whopFromCache: Boolean(whopStats.fromCache),
    },
  };
}

/** Color a rate so the weak brands stand out without reading the numbers. */
function rateTone(p) {
  if (p == null) return null;
  if (p >= 70) return 'good';
  if (p >= 40) return 'warn';
  return 'bad';
}

/** Whop statuses that still confer access, matching the server's gate. */
const ACTIVE_STATUSES = ['active', 'trialing', 'past_due', 'completed'];
const INACTIVE_STATUSES = ['canceled', 'expired', 'canceling'];

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (ACTIVE_STATUSES.includes(s)) return 'ae-status ae-status--active';
  if (INACTIVE_STATUSES.includes(s)) return 'ae-status ae-status--inactive';
  return 'ae-status ae-status--other';
}

/**
 * RiskLo signups for one brand, opened from that brand's card.
 *
 * Shows the memberships matching the configured RiskLo product filters — NOT
 * the full member roll, which is mostly people who bought something else on the
 * same Whop company. This is the panel that used to live on the Account page
 * hardcoded to Vector; the layout is deliberately unchanged, only the company it
 * reads is now the brand you opened it from.
 */
function MemberRosterModal({ entity, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(entity.slug)}/members`
        + `${refresh ? '?refresh=1' : ''}`;
      const res = await fetch(url, { headers: getBearerAuthHeaders() });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || 'Failed to load RiskLo signups');
      setData(json);
      setStatusFilter('');
      setProductFilter('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [entity.slug]);

  useEffect(() => { load(); }, [load]);

  // Close on Escape — a modal that traps you is worse than no modal.
  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const all = data?.rows || [];

  const statusOptions = [...new Set(all.map((r) => String(r.status || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  // Keyed on id+title together: two products can share a title, and a product
  // with no title still needs to be pickable.
  const productOptions = (() => {
    const map = new Map();
    all.forEach((r) => {
      const id = String(r.productId || '').trim();
      const title = String(r.productTitle || '').trim();
      const value = `${id}\t${title}`;
      if (map.has(value)) return;
      map.set(value, (title && id) ? `${title} (${id})` : title || id || 'Unknown product');
    });
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  })();

  const needle = statusFilter.trim().toLowerCase();
  const rows = all.filter((r) => {
    if (needle && String(r.status || '').trim().toLowerCase() !== needle) return false;
    if (productFilter) {
      const id = String(r.productId || '').trim();
      const title = String(r.productTitle || '').trim();
      if (`${id}\t${title}` !== productFilter) return false;
    }
    return true;
  });
  const filtersActive = Boolean(statusFilter || productFilter);

  const memberLabel = (r) => r.email
    || (r.username ? `@${r.username}` : null)
    || r.name
    || r.userId
    || (r.membershipId ? `Membership ${r.membershipId}` : null)
    || '—';

  return (
    <div className="ae-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ae-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="ae-modal-head">
          <h2 className="ae-modal-title">{entity.displayName} — RiskLo signups</h2>
          <button type="button" className="ae-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <p className="ae-modal-intro">
          Memberships on {entity.displayName}&apos;s Whop company that match your configured
          RiskLo product IDs or titles (same filters as Pro access). Successful
          responses are cached for about five minutes on the server to limit Whop
          API calls; use Refresh to pull the latest immediately.
        </p>

        <div className="ae-modal-toolbar">
          <button type="button" className="ae-btn" onClick={() => load(true)} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh from Whop'}
          </button>
          {data?.fetchedAt && !loading ? (
            <span className="ae-modal-meta">
              {data.fromCache ? 'Cached · ' : ''}Data from Whop {fmtDate(data.fetchedAt)}
            </span>
          ) : null}
        </div>

        {error ? <div className="ae-error" role="alert">{error}</div> : null}
        {!error && data && !data.whopConfigured ? (
          <p className="ae-empty">This brand has no Whop company configured.</p>
        ) : null}

        {!loading && !error && data?.whopConfigured ? (
          <>
            <div className="ae-modal-stats">
              <Stat label="Distinct emails (active access)" value={fmtNum(data.distinctEmailsActive)} />
              <Stat label="Membership rows (active access)" value={fmtNum(data.activeRowCount)} />
              <Stat label="All matching rows (any status)" value={fmtNum(data.matchingRows)} />
              <Stat
                label={data.fetchScope === 'product_ids'
                  ? 'Rows fetched (RiskLo product)'
                  : 'Company rows scanned'}
                value={fmtNum(data.rowsFetched)}
              />
            </div>
            <p className="ae-modal-meta">
              Product filters: {fmtNum(data.productIdsConfigured)} ID(s),{' '}
              {fmtNum(data.productTitlesConfigured)} title(s)
              {data.pagesFetched ? ` · Pages: ${fmtNum(data.pagesFetched)}` : ''}
              {data.fetchScope === 'product_ids'
                ? ' · Whop requests filtered by product ID (lightweight vs full company scan).'
                : ''}
              {data.hasMorePages
                ? ' (list may be truncated; increase max pages on server if needed)'
                : ''}
            </p>
          </>
        ) : null}

        <div className="ae-modal-body">
          {loading ? <p className="ae-empty">Loading Whop data…</p> : null}
          {!loading && !error && data?.whopConfigured && !all.length ? (
            <p className="ae-empty">
              No memberships matched the configured RiskLo product filters yet.
            </p>
          ) : null}

          {!loading && all.length ? (
            <>
              <div className="ae-modal-filters">
                <label className="ae-modal-field">
                  <span className="ae-modal-field-label">Status</span>
                  <select
                    className="ae-modal-select"
                    value={statusFilter}
                    onChange={(ev) => setStatusFilter(ev.target.value)}
                    aria-label="Filter table by membership status"
                  >
                    <option value="">All statuses</option>
                    {statusOptions.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </label>
                <label className="ae-modal-field">
                  <span className="ae-modal-field-label">Product</span>
                  <select
                    className="ae-modal-select"
                    value={productFilter}
                    onChange={(ev) => setProductFilter(ev.target.value)}
                    aria-label="Filter table by product"
                  >
                    <option value="">All products</option>
                    {productOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                {filtersActive ? (
                  <button
                    type="button"
                    className="ae-modal-clear"
                    onClick={() => { setStatusFilter(''); setProductFilter(''); }}
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>

              {filtersActive ? (
                <p className="ae-modal-meta">
                  Showing {fmtNum(rows.length)} of {fmtNum(all.length)}{' '}
                  {all.length === 1 ? 'row' : 'rows'}
                </p>
              ) : null}

              <table className="ae-modal-table">
                <thead>
                  <tr>
                    <th scope="col">Member</th>
                    <th scope="col">Status</th>
                    <th scope="col">Product</th>
                    <th scope="col">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.membershipId || r.userId || r.email || 'row'}-${i}`}>
                      <td>{memberLabel(r)}</td>
                      <td><span className={statusClass(r.status)}>{r.status || '—'}</span></td>
                      <td>{r.productTitle || r.productId || '—'}</td>
                      <td>{r.createdAt ? fmtDate(r.createdAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtersActive && !rows.length ? (
                <p className="ae-empty">No rows match the selected filters.</p>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Active Whop members for one brand — every entitled membership on that company's
 * Whop account (all products). This is who the access gate admits via Whop.
 */
function ActiveMembersModal({ entity, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [entitledOnly, setEntitledOnly] = useState(true);
  const [productFilter, setProductFilter] = useState('');

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ view: 'active-whop' });
      if (refresh) params.set('refresh', '1');
      const url = `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(entity.slug)}/members?${params}`;
      const res = await fetch(url, { headers: getBearerAuthHeaders() });
      const json = await res.json();
      if (!res.ok || !json.success) {
        const msg = res.status === 404
          ? 'Endpoint not found — restart the API server (port 5001) to pick up this route.'
          : (json.error || 'Failed to load active Whop members');
        throw new Error(msg);
      }
      if (json.view !== 'active-whop') {
        throw new Error('Restart the API server (port 5001) to load active Whop members support.');
      }
      setData(json);
      setStatusFilter('');
      setProductFilter('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [entity.slug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const all = data?.rows || [];

  const statusOptions = [...new Set(all.map((r) => String(r.status || '').trim()).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  const productOptions = (() => {
    const map = new Map();
    all.forEach((r) => {
      const id = String(r.productId || '').trim();
      const title = String(r.productTitle || '').trim();
      const value = `${id}\t${title}`;
      if (map.has(value)) return;
      map.set(value, (title && id) ? `${title} (${id})` : title || id || 'Unknown product');
    });
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  })();

  const needle = statusFilter.trim().toLowerCase();
  const rows = all.filter((r) => {
    if (entitledOnly && !r.entitled) return false;
    if (needle && String(r.status || '').trim().toLowerCase() !== needle) return false;
    if (productFilter) {
      const id = String(r.productId || '').trim();
      const title = String(r.productTitle || '').trim();
      if (`${id}\t${title}` !== productFilter) return false;
    }
    return true;
  });
  const filtersActive = Boolean(statusFilter || productFilter || !entitledOnly);

  const memberLabel = (r) => r.email
    || (r.username ? `@${r.username}` : null)
    || r.name
    || r.userId
    || (r.membershipId ? `Membership ${r.membershipId}` : null)
    || '—';

  return (
    <div className="ae-modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ae-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="ae-modal-head">
          <h2 className="ae-modal-title">{entity.displayName} — Active Whop members</h2>
          <button type="button" className="ae-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <p className="ae-modal-intro">
          Every membership on {entity.displayName}&apos;s Whop company with entitled status
          (same rule as the brand access gate — all products, not just RiskLo Pro).
          Use this to validate who gets in via Whop. Responses are cached for about
          five minutes; use Refresh to pull the latest immediately.
        </p>

        <div className="ae-modal-toolbar">
          <button type="button" className="ae-btn" onClick={() => load(true)} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh from Whop'}
          </button>
          {data?.fetchedAt && !loading ? (
            <span className="ae-modal-meta">
              {data.fromCache ? 'Cached · ' : ''}Data from Whop {fmtDate(data.fetchedAt)}
            </span>
          ) : null}
        </div>

        {error ? <div className="ae-error" role="alert">{error}</div> : null}
        {!error && data && !data.whopConfigured ? (
          <p className="ae-empty">This brand has no Whop company configured.</p>
        ) : null}

        {!loading && !error && data?.whopConfigured ? (
          <>
            <div className="ae-modal-stats">
              <Stat label="Distinct emails (entitled)" value={fmtNum(data.distinctEmailsActive)} />
              <Stat label="Entitled membership rows" value={fmtNum(data.activeRowCount)} />
              <Stat label="All membership rows" value={fmtNum(data.totalRows)} />
              <Stat
                label="Unclaimed (entitled)"
                value={fmtNum(data.rowsWithoutUser)}
                tone={data.rowsWithoutUser ? 'warn' : null}
                sub="no linked account"
              />
            </div>
          </>
        ) : null}

        <div className="ae-modal-body">
          {loading ? <p className="ae-empty">Loading Whop data…</p> : null}
          {!loading && !error && data?.whopConfigured && !all.length ? (
            <p className="ae-empty">No memberships returned from Whop for this company yet.</p>
          ) : null}

          {!loading && all.length ? (
            <>
              <div className="ae-modal-filters">
                <label className="ae-modal-field ae-modal-field--checkbox">
                  <span className="ae-modal-field-label">
                    <input
                      type="checkbox"
                      checked={entitledOnly}
                      onChange={(ev) => setEntitledOnly(ev.target.checked)}
                    />
                    {' '}Entitled only (access gate)
                  </span>
                </label>
                <label className="ae-modal-field">
                  <span className="ae-modal-field-label">Status</span>
                  <select
                    className="ae-modal-select"
                    value={statusFilter}
                    onChange={(ev) => setStatusFilter(ev.target.value)}
                    aria-label="Filter table by membership status"
                  >
                    <option value="">All statuses</option>
                    {statusOptions.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </label>
                <label className="ae-modal-field">
                  <span className="ae-modal-field-label">Product</span>
                  <select
                    className="ae-modal-select"
                    value={productFilter}
                    onChange={(ev) => setProductFilter(ev.target.value)}
                    aria-label="Filter table by product"
                  >
                    <option value="">All products</option>
                    {productOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                {filtersActive ? (
                  <button
                    type="button"
                    className="ae-modal-clear"
                    onClick={() => {
                      setStatusFilter('');
                      setProductFilter('');
                      setEntitledOnly(true);
                    }}
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>

              {filtersActive ? (
                <p className="ae-modal-meta">
                  Showing {fmtNum(rows.length)} of {fmtNum(all.length)}{' '}
                  {all.length === 1 ? 'row' : 'rows'}
                </p>
              ) : null}

              <table className="ae-modal-table">
                <thead>
                  <tr>
                    <th scope="col">Member</th>
                    <th scope="col">Status</th>
                    <th scope="col">Product</th>
                    <th scope="col">RiskLo account</th>
                    <th scope="col">Manual grant</th>
                    <th scope="col">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.membershipId || r.userId || r.email || 'row'}-${i}`}>
                      <td>{memberLabel(r)}</td>
                      <td>
                        <span className={statusClass(r.status)}>{r.status || '—'}</span>
                        {r.entitled ? null : (
                          <span className="ae-stat-sub"> · not entitled</span>
                        )}
                      </td>
                      <td>{r.productTitle || r.productId || '—'}</td>
                      <td>{r.email ? (r.signedUp ? 'Yes' : 'No') : '—'}</td>
                      <td>{r.granted ? 'Yes' : '—'}</td>
                      <td>{r.createdAt ? fmtDate(r.createdAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtersActive && !rows.length ? (
                <p className="ae-empty">No rows match the selected filters.</p>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function AdminEntities({ onNavigate }) {
  const [entities, setEntities] = useState([]);
  const [platform, setPlatform] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [rosterEntity, setRosterEntity] = useState(null);
  const [activeMembersEntity, setActiveMembersEntity] = useState(null);
  // Strategy names available in the copy-source brand, for the picker below.
  const [sourceStrategies, setSourceStrategies] = useState([]);
  const [sourceStrategiesLoading, setSourceStrategiesLoading] = useState(false);
  // Distinct from an empty list: a failed request must not be reported as
  // "no strategies", which is a claim we have not established.
  const [sourceStrategiesError, setSourceStrategiesError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whopLoading, setWhopLoading] = useState(false);
  const [whopFetchedAt, setWhopFetchedAt] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [drafts, setDrafts] = useState({});
  // Feature catalog for Brands → Features (same list as Users → Feature access).
  const [featureCatalog, setFeatureCatalog] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(API_ENDPOINTS.adminFeatureAccess, { headers: getBearerAuthHeaders() });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) setFeatureCatalog(data.features || []);
      } catch (_e) { /* the section just doesn't show */ }
    })();
  }, []);
  const [savingSlug, setSavingSlug] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newEntity, setNewEntity] = useState({ slug: '', displayName: '' });
  const [copy, setCopy] = useState({ from: 'vector', to: '', strategyName: '' });
  const [portfolioCopy, setPortfolioCopy] = useState({ from: 'vector', to: '', includeOg: true, mirrorLive: true });
  const [copyingPortfolio, setCopyingPortfolio] = useState(false);
  const [expandedSlugs, setExpandedSlugs] = useState(() => new Set());

  const toggleBrandExpanded = (slug) => {
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  /**
   * Phase 1: Postgres stats only — page renders immediately.
   * Phase 2: Whop metrics (24h cache; refresh button bypasses).
   */
  const loadWhopMetrics = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true); else setWhopLoading(true);
    try {
      const url = `${API_BASE_URL}/api/entities/admin/whop-metrics${refresh ? '?refresh=1' : ''}`;
      const res = await fetch(url, { headers: getBearerAuthHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load Whop member metrics');
      const bySlug = new Map((data.metrics || []).map((row) => [row.slug, row.whopStats]));
      setEntities((prev) => prev.map((e) => mergeWhopStatsIntoEntity(e, bySlug.get(e.slug))));
      setWhopFetchedAt(data.fetchedAt || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setWhopLoading(false);
      setRefreshing(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/admin`, { headers: getBearerAuthHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load brands');
      setEntities(data.entities || []);
      setPlatform(data.platform || null);
      const next = {};
      (data.entities || []).forEach((e) => {
        next[e.slug] = {
          displayName: e.displayName || '',
          logoUrl: e.branding?.logoUrl || '',
          resultsLabel: e.branding?.resultsLabel || '',
          accentColor: e.branding?.accentColor || '',
          appName: e.branding?.appName || '',
          tagline: e.branding?.tagline || '',
          legalName: e.branding?.legalName || '',
          supportEmail: e.branding?.supportEmail || '',
          emailFromName: e.branding?.emailFromName || '',
          faviconUrl: e.branding?.faviconUrl || '',
          layoutPreset: e.branding?.layout?.preset || 'classic',
          layoutMode: e.branding?.layout?.defaultMode || '',
          // Fonts are set on Brand Settings; carried through so saving here keeps them.
          layoutFonts: e.branding?.layout?.fonts || null,
          whopCompanyId: e.whopCompanyId || '',
          whopApiKeyEnv: e.whopApiKeyEnv || '',
          checkoutUrl: e.checkoutUrl || '',
          tradeWatchSupportEmails: (e.tradeWatchSupportEmails || []).join(', '),
          lockedFeatures: e.lockedFeatures || [],
          theme: e.branding?.theme || null,
          embedWidget: normalizeEmbedWidget(e.branding?.embedWidget || {}),
        };
      });
      setDrafts(next);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading) return undefined;
    loadWhopMetrics();
    return undefined;
  }, [loading, loadWhopMetrics]);

  // The copy picker lists what the SOURCE brand actually has. Typing a name
  // from memory meant discovering it was wrong only after the copy failed.
  useEffect(() => {
    if (!copy.from) { setSourceStrategies([]); setSourceStrategiesError(null); return undefined; }
    let cancelled = false;
    setSourceStrategiesLoading(true);
    setSourceStrategiesError(null);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(copy.from)}/strategies`,
          { headers: getBearerAuthHeaders() },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.success) {
          // A 404 here usually means the server predates this endpoint and needs
          // a restart — worth saying, rather than claiming the brand is empty.
          setSourceStrategies([]);
          setSourceStrategiesError(
            res.status === 404
              ? 'Endpoint not found — restart the server to pick up this route.'
              : (data.error || `Couldn't load strategies (HTTP ${res.status})`),
          );
          return;
        }
        setSourceStrategies(data.strategies || []);
      } catch (e) {
        if (!cancelled) {
          setSourceStrategies([]);
          setSourceStrategiesError(e.message || "Couldn't load strategies");
        }
      } finally {
        if (!cancelled) setSourceStrategiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [copy.from]);

  // A name carried over from the previous source brand would not exist here.
  useEffect(() => {
    setCopy((c) => (c.strategyName ? { ...c, strategyName: '' } : c));
  }, [copy.from]);

  const patch = (slug, key, value) => {
    setDrafts((prev) => ({ ...prev, [slug]: { ...prev[slug], [key]: value } }));
  };

  const save = async (slug) => {
    const d = drafts[slug];
    setSavingSlug(slug);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/admin/${encodeURIComponent(slug)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          displayName: d.displayName,
          // Sent explicitly (even when blank) so clearing a value works —
          // the API distinguishes "absent" from "set to empty".
          whopCompanyId: d.whopCompanyId || null,
          whopApiKeyEnv: d.whopApiKeyEnv || null,
          checkoutUrl: d.checkoutUrl || null,
          tradeWatchSupportEmails: d.tradeWatchSupportEmails || '',
          lockedFeatures: d.lockedFeatures || [],
          branding: {
            logoUrl: d.logoUrl || undefined,
            resultsLabel: d.resultsLabel || undefined,
            accentColor: d.accentColor || undefined,
            // Identity: always sent, so a blank field clears it.
            appName: d.appName || '',
            tagline: d.tagline || '',
            legalName: d.legalName || '',
            supportEmail: d.supportEmail || '',
            emailFromName: d.emailFromName || '',
            faviconUrl: d.faviconUrl || '',
            layout: { preset: d.layoutPreset || 'classic', ...(d.layoutMode ? { defaultMode: d.layoutMode } : {}), ...(d.layoutFonts ? { fonts: d.layoutFonts } : {}) },
            embedWidget: d.embedWidget || undefined,
            // null clears it (Reset to RiskLo).
            theme: d.theme && d.theme.primary ? d.theme : null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setNotice(`Saved ${d.displayName}.`);
      // Editing the brand you're on: show its new theme straight away.
      // Editing the brand you're on: show what its users now see (on its plan).
      if ((data.preview || data.entity) && getActiveEntitySlug() === slug) setActiveEntityBrand(data.preview || data.entity);
      window.dispatchEvent(new Event(FEATURE_ACCESS_CHANGED_EVENT));
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingSlug(null);
    }
  };

  /** Move a brand to another plan; it unlocks (or hides) branding straight away. */
  const setBrandPlan = async (e, plan) => {
    const option = PLAN_OPTIONS.find((o) => o.value === plan);
    if (!option || plan === (e.plan || 'starter')) return;
    if (!window.confirm(`Move ${e.displayName} to ${option.label}?\n\n${option.unlocks}`)) return;
    setSavingSlug(e.slug);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/admin/${encodeURIComponent(e.slug)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ plan }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not change the plan');
      setNotice(`${e.displayName} is on ${option.label.split(' ·')[0]}.`);
      if (data.preview && getActiveEntitySlug() === e.slug) setActiveEntityBrand(data.preview);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSlug(null);
    }
  };

  /** Legacy deal: keep colors & layout whatever the plan, and a private note. */
  const saveLegacyDeal = async (e, planExtras, planNote) => {
    setSavingSlug(e.slug);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/admin/${encodeURIComponent(e.slug)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ planExtras, planNote }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not save the legacy deal');
      setNotice(`Saved ${e.displayName}’s legacy deal.`);
      if (data.preview && getActiveEntitySlug() === e.slug) setActiveEntityBrand(data.preview);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSlug(null);
    }
  };

  /** Switch a brand on or off (the RiskLo home brand starts off; see migration 110). */
  const setBrandActive = async (e, on) => {
    const home = e.slug === 'risklo';
    const question = on
      ? (home
        ? 'Switch on the RiskLo home brand?\n\nEveryone who signs in without a company (no Vector, GemAlgo, …) lands here from now on, '
          + 'and sees only its strategies. Copy strategies in first.'
        : `Switch on ${e.displayName}? Its members can use it again.`)
      : (home
        ? 'Switch off the RiskLo home brand? People with no company go back to having no brand.'
        : `Switch off ${e.displayName}? Its members lose access until it is switched back on.`);
    if (!window.confirm(question)) return;
    setSavingSlug(e.slug);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/admin/${encodeURIComponent(e.slug)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ isActive: on }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not change the brand');
      setNotice(`${e.displayName} is ${on ? 'on' : 'off'}.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSlug(null);
    }
  };

  const createEntity = async () => {
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/entities/admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify(newEntity),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Create failed');
      setNotice(`Created "${newEntity.displayName}". It starts with no strategies.`);
      setNewEntity({ slug: '', displayName: '' });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const copyStrategy = async () => {
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(copy.to)}/copy-strategy`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
          body: JSON.stringify({ fromEntity: copy.from, strategyName: copy.strategyName }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Copy failed');
      const parts = [];
      if (data.copiedSnapshot) parts.push('weekday grid');
      if (data.copiedEquityCurve) parts.push('equity curve');
      if (data.copiedTrades) parts.push(`${fmtNum(data.copiedTrades)} trades`);
      if (!data.copiedScenario) {
        setError(
          `Copied "${data.strategyName}" into ${data.to}, but no strategy entry could be `
          + 'created, so it will not appear on Overview or Results yet'
          + `${data.scenarioError ? ` — ${data.scenarioError}` : '.'}`,
        );
      } else {
        setNotice(
          `Copied "${data.strategyName}" from ${data.from} → ${data.to}`
          + `${parts.length ? ` (${parts.join(', ')})` : ''}. `
          + (data.provisionedScenario
            ? 'A strategy entry was created for it, so it shows on Overview and Results. '
            : '')
          + 'It is now an independent copy — edits on one side do not affect the other.',
        );
      }
      setCopy((c) => ({ ...c, strategyName: '' }));
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const copyEquityPortfolio = async () => {
    setError(null);
    setNotice(null);
    setCopyingPortfolio(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(portfolioCopy.to)}/copy-equity-portfolio`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
          body: JSON.stringify({
            fromEntity: portfolioCopy.from,
            includeOg: portfolioCopy.includeOg,
            mirrorLive: portfolioCopy.mirrorLive,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Copy failed');
      const legSummary = `${fmtNum(data.legsCopied)} leg(s) copied`
        + (data.legsSkipped ? `, ${fmtNum(data.legsSkipped)} already present` : '');
      const scenarioSummary = (data.scenariosCopied?.length
        ? `${data.scenariosCopied.length} portfolio scenario(s)`
        : 'no new portfolio scenarios');
      if (data.legErrors?.length) {
        setError(
          `Portfolio mirror partially completed (${legSummary}). `
          + `${data.legErrors.length} leg(s) failed: ${data.legErrors.map((e) => e.strategyName).join(', ')}.`,
        );
      } else {
        setNotice(
          `Mirrored Equity Portfolio from ${data.from} → ${data.to}: ${legSummary}; `
          + `${fmtNum(data.configsCopied)} config row(s); ${scenarioSummary}. `
          + (data.mirrorLive
            ? 'Live-wired legs follow the source brand automatically.'
            : 'Leg curves are static copies.'),
        );
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setCopyingPortfolio(false);
    }
  };

  return (
    <div className="admin-entities">
      <div className="ae-header">
        <button type="button" className="ae-back" onClick={() => onNavigate && onNavigate('dashboard')}>
          ← Back
        </button>
        <h1 className="ae-title">Brands</h1>
        <button
          type="button"
          className="ae-btn ae-refresh"
          onClick={() => loadWhopMetrics({ refresh: true })}
          disabled={refreshing || whopLoading || loading}
        >
          {refreshing ? 'Refreshing from Whop…' : 'Refresh from Whop'}
        </button>
      </div>
      <p className="ae-intro">
        Brands hosted on RiskLo. Each brand has its own strategies and members;
        only the top Admin tier spans all of them. Creating a brand gives you an
        empty workspace — the pages are all there, but no strategies until you add them.
        {whopFetchedAt && !whopLoading ? (
          <span className="ae-whop-cache-note">
            {' '}Whop member counts as of {fmtDate(whopFetchedAt)} (cached up to 24h — use Refresh for live).
          </span>
        ) : null}
      </p>

      {platform ? (
        <section className="ae-platform">
          <h2 className="ae-platform-title">RiskLo overall</h2>
          <div className="ae-stats">
            <Stat label="RiskLo accounts" value={fmtNum(platform.riskloAccounts)} />
            <Stat
              label={`Active (${platform.activeWindowDays}d)`}
              value={fmtNum(platform.riskloActive)}
              sub={fmtPct(platform.riskloActiveRate)}
              tone={rateTone(platform.riskloActiveRate)}
            />
            <Stat label={`New (${platform.activeWindowDays}d)`} value={fmtNum(platform.riskloNew)} />
            <Stat
              label="Email verified"
              value={fmtNum(platform.riskloVerified)}
              sub={fmtPct(platform.riskloVerifiedRate)}
              tone={rateTone(platform.riskloVerifiedRate)}
            />
            <Stat label="Brands" value={fmtNum(platform.brands)} />
          </div>
          <p className="ae-platform-note">
            Accounts are global — one RiskLo login can belong to several brands, so the per-brand
            member counts below overlap and will not sum to this total.
          </p>
        </section>
      ) : null}

      {error ? <div className="ae-error" role="alert">{error}</div> : null}
      {notice ? <div className="ae-notice" role="status">{notice}</div> : null}

      <GlobalSettingsPanel />

      <CompaniesPanel entities={entities} onChanged={load} />

      <BrandInvitesPanel onApproved={load} />

      {rosterEntity ? (
        <MemberRosterModal entity={rosterEntity} onClose={() => setRosterEntity(null)} />
      ) : null}

      {activeMembersEntity ? (
        <ActiveMembersModal
          entity={activeMembersEntity}
          onClose={() => setActiveMembersEntity(null)}
        />
      ) : null}

      {loading ? <p className="ae-empty">Loading…</p> : (
        <>
          {entities.map((e) => {
            const d = drafts[e.slug] || {};
            const isExpanded = expandedSlugs.has(e.slug);
            return (
              <section
                className={`ae-card${isExpanded ? ' is-expanded' : ' is-collapsed'}`}
                key={e.slug}
              >
                <div className="ae-card-head">
                  {d.logoUrl
                    ? <img src={d.logoUrl} alt="" className="ae-logo" />
                    : <span className="ae-logo ae-logo--fallback">{(e.displayName || '?').charAt(0)}</span>}
                  <div className="ae-card-head-copy">
                    <h2 className="ae-card-title">{e.displayName}</h2>
                    <code className="ae-slug">{e.slug}</code>
                  </div>
                  {e.slug !== 'risklo' ? (
                    <label className="ae-plan-picker">
                      <span>Plan</span>
                      <select
                        value={e.plan || 'starter'}
                        disabled={savingSlug === e.slug}
                        onChange={(ev) => setBrandPlan(e, ev.target.value)}
                      >
                        {PLAN_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </label>
                  ) : null}
                  {(e.planExtras || []).length || e.planNote ? (
                    <span className="ae-legacy-pill" title={e.planNote || 'Keeps colors & layout whatever the plan'}>Legacy deal</span>
                  ) : null}
                  <label className={`ae-active-toggle ${e.isActive === false ? 'is-off' : ''}`}>
                    <input
                      type="checkbox"
                      checked={e.isActive !== false}
                      disabled={savingSlug === e.slug}
                      onChange={(ev) => setBrandActive(e, ev.target.checked)}
                    />
                    {e.isActive === false ? 'Switched off' : 'Active'}
                  </label>
                  <span className={`ae-paywall ${e.whopConfigured ? 'is-live' : ''}`}>
                    {e.whopConfigured ? 'Whop paywall live' : 'No paywall (grants only)'}
                  </span>
                  {e.whopConfigured ? (
                    <>
                      <button
                        type="button"
                        className="ae-btn ae-view-members"
                        onClick={() => setActiveMembersEntity(e)}
                      >
                        Active members
                      </button>
                      <button
                        type="button"
                        className="ae-btn ae-view-members"
                        onClick={() => setRosterEntity(e)}
                      >
                        RiskLo signups
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="ae-btn ae-card-toggle"
                    aria-expanded={isExpanded}
                    onClick={() => toggleBrandExpanded(e.slug)}
                  >
                    {isExpanded ? 'Collapse' : 'Expand'}
                  </button>
                </div>

                <h3 className="ae-stat-group ae-stat-group--compact">
                  Brand members
                  {whopLoading && e.whopConfigured ? (
                    <span className="ae-stat-group-note">Loading Whop counts…</span>
                  ) : null}
                  {isExpanded ? (
                    <span className="ae-stat-group-note">
                      everyone with access to this brand — the access gate does not
                      filter by product
                    </span>
                  ) : null}
                </h3>
                <div className="ae-stats">
                  <Stat
                    label="Total members"
                    value={fmtNum(e.stats?.members?.total)}
                    sub="Whop active + granted"
                  />
                  <Stat
                    label="Whop active"
                    value={fmtNum(e.stats?.members?.whopActive)}
                    sub={e.stats?.members?.whopConfigured
                      ? `${fmtNum(e.stats?.members?.whopMembershipRows)} rows`
                      : 'no paywall'}
                  />
                  {e.stats?.members?.rowsWithoutUser ? (
                    <Stat
                      label="Unclaimed"
                      value={fmtNum(e.stats?.members?.rowsWithoutUser)}
                      tone="warn"
                      sub="no linked account"
                    />
                  ) : null}
                  <Stat label="Granted" value={fmtNum(e.stats?.members?.granted)} />
                  <Stat
                    label="Signed up to RiskLo"
                    value={fmtNum(e.stats?.signups?.signedUp)}
                    sub={`${fmtNum(e.stats?.signups?.notSignedUp)} never did`}
                  />
                  <Stat
                    label="Activation rate"
                    value={fmtPct(e.stats?.signups?.activationRate)}
                    tone={rateTone(e.stats?.signups?.activationRate)}
                    sub="of members with an account"
                  />
                  <Stat
                    label={`Active (${e.stats?.signups?.activeWindowDays ?? 30}d)`}
                    value={fmtNum(e.stats?.signups?.activeRecently)}
                    sub={fmtPct(e.stats?.signups?.activeRate)}
                    tone={rateTone(e.stats?.signups?.activeRate)}
                  />
                </div>

                {e.stats?.riskLoProduct?.filtersConfigured ? (
                  <>
                    <h3 className="ae-stat-group ae-stat-group--compact">
                      RiskLo product buyers
                      {isExpanded ? (
                        <span className="ae-stat-group-note">
                          only memberships matching the configured RiskLo product —
                          the same population as Users → RiskLo signups
                        </span>
                      ) : null}
                    </h3>
                    <div className="ae-stats">
                      <Stat
                        label="Product members"
                        value={fmtNum(e.stats?.riskLoProduct?.members)}
                        sub={`${fmtNum(e.stats?.riskLoProduct?.membershipRows)} rows`}
                      />
                      <Stat
                        label="Signed up to RiskLo"
                        value={fmtNum(e.stats?.riskLoProduct?.signedUp)}
                        sub={`${fmtNum(e.stats?.riskLoProduct?.notSignedUp)} never did`}
                      />
                      <Stat
                        label="Activation rate"
                        value={fmtPct(e.stats?.riskLoProduct?.activationRate)}
                        tone={rateTone(e.stats?.riskLoProduct?.activationRate)}
                        sub="of product buyers"
                      />
                      <Stat
                        label={`Active (${e.stats?.signups?.activeWindowDays ?? 30}d)`}
                        value={fmtNum(e.stats?.riskLoProduct?.activeRecently)}
                        sub={fmtPct(e.stats?.riskLoProduct?.activeRate)}
                        tone={rateTone(e.stats?.riskLoProduct?.activeRate)}
                      />
                    </div>
                  </>
                ) : null}

                {!isExpanded ? (
                  <button
                    type="button"
                    className="ae-card-expand-hint"
                    onClick={() => toggleBrandExpanded(e.slug)}
                  >
                    Expand for community stats, content, branding, NT8 webhook &amp; embed widget
                  </button>
                ) : null}

                {isExpanded ? (
                  <div className="ae-card-expanded">
                {/* Status breakdown: makes the member number self-explaining.
                    Whop's company page shows a "joined" count that includes
                    people with no membership row, so the two rarely match and
                    the difference is otherwise invisible. */}
                {e.stats?.members?.whopConfigured
                  && Object.keys(e.stats?.members?.whopByStatus || {}).length ? (
                    <p className="ae-status-breakdown">
                      Whop rows by status:{' '}
                      {Object.entries(e.stats.members.whopByStatus)
                        .sort((a2, b2) => b2[1] - a2[1])
                        .map(([status, n]) => `${status} ${fmtNum(n)}`)
                        .join(' · ')}
                    </p>
                  ) : null}
                {e.stats?.members?.whopError ? (
                  <p className="ae-stat-warning" role="status">
                    Whop lookup unavailable: {e.stats.members.whopError} — member counts below
                    reflect manual grants only.
                  </p>
                ) : null}

                {e.stats?.community?.joined ? (
                  <>
                    <h3 className="ae-stat-group">
                      Community
                      <span className="ae-stat-group-note">
                        everyone who joined this Whop — the &ldquo;N joined&rdquo; figure on the
                        company page. Includes people with no purchase, so it is larger than
                        the membership count above.
                      </span>
                    </h3>
                    <div className="ae-stats">
                      <Stat
                        label="Joined"
                        value={fmtNum(e.stats?.community?.joined)}
                        sub={e.stats?.community?.withoutEmail
                          ? `${fmtNum(e.stats.community.withoutEmail)} without an email`
                          : 'all have an email'}
                      />
                      <Stat
                        label="Signed up to RiskLo"
                        value={fmtNum(e.stats?.community?.signedUp)}
                        sub={`${fmtNum(e.stats?.community?.notSignedUp)} never did`}
                      />
                      <Stat
                        label="Activation rate"
                        value={fmtPct(e.stats?.community?.activationRate)}
                        tone={rateTone(e.stats?.community?.activationRate)}
                        sub="of joiners with an email"
                      />
                      <Stat
                        label={`Active (${e.stats?.signups?.activeWindowDays ?? 30}d)`}
                        value={fmtNum(e.stats?.community?.activeRecently)}
                        sub={fmtPct(e.stats?.community?.activeRate)}
                        tone={rateTone(e.stats?.community?.activeRate)}
                      />
                    </div>
                    {Object.keys(e.stats?.community?.byAccessLevel || {}).length ? (
                      <p className="ae-status-breakdown">
                        Joiners by access level:{' '}
                        {Object.entries(e.stats.community.byAccessLevel)
                          .sort((a2, b2) => b2[1] - a2[1])
                          .map(([level, n]) => `${level.replace(/_/g, ' ')} ${fmtNum(n)}`)
                          .join(' · ')}
                      </p>
                    ) : null}
                  </>
                ) : null}

                <h3 className="ae-stat-group">Content</h3>
                <div className="ae-stats">
                  <Stat label="Strategies" value={fmtNum(e.stats?.strategies)} />
                  <Stat
                    label="Registry scenarios"
                    value={fmtNum(e.stats?.scenarios)}
                    sub={`${fmtNum(e.stats?.internalScenarios)} internal`}
                  />
                  <Stat label="Equity strategies" value={fmtNum(e.stats?.equityStrategies)} />
                  <Stat label="Journal trades" value={fmtNum(e.stats?.journalTrades)} />
                  <Stat
                    label="With trade data"
                    value={fmtPct(e.stats?.coverage?.dataCoverageRate)}
                    tone={rateTone(e.stats?.coverage?.dataCoverageRate)}
                    sub={`${fmtNum(e.stats?.coverage?.strategiesWithoutTrades)} empty`}
                  />
                </div>
                <div className="ae-timestamps">
                  <span>Last sync: {fmtDate(e.stats?.lastSyncAt)}</span>
                  <span>Last trade update: {fmtDate(e.stats?.lastTradeAt)}</span>
                  {e.stats?.members?.fromCache ? <span>Whop counts cached (≤24h)</span> : null}
                  {e.stats?.whopFetchedAt ? (
                    <span>Whop data: {fmtDate(e.stats.whopFetchedAt)}</span>
                  ) : null}
                </div>

                <div className="ae-fields">
                  <label>
                    <span>Display name</span>
                    <input value={d.displayName || ''} onChange={(ev) => patch(e.slug, 'displayName', ev.target.value)} />
                  </label>
                  <label>
                    <span>Results nav label</span>
                    <input
                      placeholder={`${e.displayName} Results`}
                      value={d.resultsLabel || ''}
                      onChange={(ev) => patch(e.slug, 'resultsLabel', ev.target.value)}
                    />
                  </label>
                  <label className="ae-logo-field">
                    <span>Logo</span>
                    <div className="ae-logo-upload">
                      {d.logoUrl && isUploadedLogo(d.logoUrl) ? (
                        <div className="ae-logo-upload-preview">
                          <img src={d.logoUrl} alt="" className="ae-logo ae-logo--preview" />
                          <button
                            type="button"
                            className="ae-btn ae-btn-ghost"
                            onClick={() => patch(e.slug, 'logoUrl', '')}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                      <input
                        type="file"
                        accept="image/png,.png"
                        onChange={async (ev) => {
                          const file = ev.target.files?.[0];
                          ev.target.value = '';
                          if (!file) return;
                          setError(null);
                          try {
                            const dataUrl = await readPngLogoAsDataUrl(file);
                            patch(e.slug, 'logoUrl', dataUrl);
                          } catch (err) {
                            setError(err.message || 'Could not upload logo.');
                          }
                        }}
                      />
                      {!isUploadedLogo(d.logoUrl) ? (
                        <input
                          placeholder="Or paste a logo URL (https://…/logo.png)"
                          value={d.logoUrl || ''}
                          onChange={(ev) => patch(e.slug, 'logoUrl', ev.target.value)}
                        />
                      ) : null}
                      <small className="ae-field-note">
                        Upload a PNG (max 512 KB) or paste an external URL. Used in the brand picker and sidebar.
                      </small>
                    </div>
                  </label>
                  <label>
                    <span>Accent color</span>
                    <input placeholder="#22c55e" value={d.accentColor || ''} onChange={(ev) => patch(e.slug, 'accentColor', ev.target.value)} />
                  </label>
                  <div className="ae-identity">
                    <div className="ae-identity-head">
                      <strong>Identity &amp; look</strong>
                      <small className="ae-field-note">
                        {PLAN_OPTIONS.find((o) => o.value === (e.plan || 'starter'))?.unlocks}
                      </small>
                      {PLAN_RANK[e.plan || 'starter'] < PLAN_RANK.whitelabel ? (
                        <small className="ae-plan-note">
                          App name, tagline, legal name, sender name and tab icon are saved but only show on White-label
                          {PLAN_RANK[e.plan || 'starter'] < PLAN_RANK.brand && !(e.planExtras || []).includes('theme') ? '; the theme and layout style only on Brand and up' : ''}.
                        </small>
                      ) : null}
                    </div>
                    {e.slug !== 'risklo' ? <LegacyDeal key={`${e.slug}:${(e.planExtras || []).join(',')}:${e.planNote || ''}`} entity={e} busy={savingSlug === e.slug} onSave={(extras, note) => saveLegacyDeal(e, extras, note)} /> : null}
                    {IDENTITY_FIELDS.map(([key, label, note]) => (
                      <label key={key}>
                        <span>{label}</span>
                        <input
                          value={d[key] || ''}
                          placeholder={key === 'appName' ? (d.displayName || e.displayName) : ''}
                          onChange={(ev) => patch(e.slug, key, ev.target.value)}
                        />
                        <small className="ae-field-note">{note}</small>
                      </label>
                    ))}
                    <label className="ae-logo-field">
                      <span>Browser tab icon</span>
                      <div className="ae-logo-upload">
                        {d.faviconUrl ? (
                          <div className="ae-logo-upload-preview">
                            <img src={d.faviconUrl} alt="" className="ae-favicon-preview" />
                            <button type="button" className="ae-btn ae-btn-ghost" onClick={() => patch(e.slug, 'faviconUrl', '')}>
                              Remove
                            </button>
                          </div>
                        ) : null}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (ev) => {
                            const file = ev.target.files?.[0];
                            ev.target.value = '';
                            if (!file) return;
                            setError(null);
                            try {
                              patch(e.slug, 'faviconUrl', await readImageAsFavicon(file));
                            } catch (err) {
                              setError(err.message || 'Could not use that icon.');
                            }
                          }}
                        />
                        <small className="ae-field-note">Any image; shrunk to 64×64. Blank = the logo.</small>
                      </div>
                    </label>
                    <label>
                      <span>Layout style</span>
                      <select value={d.layoutPreset || 'classic'} onChange={(ev) => patch(e.slug, 'layoutPreset', ev.target.value)}>
                        <option value="classic">Classic: glassy, rounded (RiskLo&apos;s look)</option>
                        <option value="clean">Clean: soft, very rounded, floating cards</option>
                        <option value="sharp">Sharp: square, flat, dense</option>
                      </select>
                      <small className="ae-field-note">Changes shapes, type and spacing, so brands don&apos;t look like recolors of each other.</small>
                    </label>
                    <label>
                      <span>First-visit mode</span>
                      <select value={d.layoutMode || ''} onChange={(ev) => patch(e.slug, 'layoutMode', ev.target.value)}>
                        <option value="">Dark (default)</option>
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                      </select>
                      <small className="ae-field-note">Users can still switch; their choice is remembered.</small>
                    </label>
                    <label>
                      <span>Sign-in link</span>
                      <div className="ae-inline">
                        <input readOnly value={`${window.location.origin}/b/${e.slug}`} onFocus={(ev) => ev.target.select()} />
                        <button
                          type="button"
                          className="ae-btn ae-btn-ghost"
                          onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/b/${e.slug}`).then(() => setNotice('Sign-in link copied.')).catch(() => {})}
                        >
                          Copy
                        </button>
                      </div>
                      <small className="ae-field-note">
                        Give this to the brand&apos;s clients: the sign-in page shows the brand before they log in.
                      </small>
                    </label>
                  </div>
                  <label>
                    <span>Whop company ID</span>
                    <input
                      placeholder="biz_… (blank = no paywall)"
                      value={d.whopCompanyId || ''}
                      onChange={(ev) => patch(e.slug, 'whopCompanyId', ev.target.value)}
                    />
                  </label>
                  <label>
                    <span>Whop API key — env var NAME</span>
                    <input
                      placeholder="WHOP_KAIROS_API_KEY (blank = shared key)"
                      value={d.whopApiKeyEnv || ''}
                      onChange={(ev) => patch(e.slug, 'whopApiKeyEnv', ev.target.value)}
                    />
                    <small className="ae-field-note">
                      The NAME of a server environment variable, never the key itself.
                      {' '}
                      {{
                        entity: `Resolving from ${d.whopApiKeyEnv}.`,
                        missing: `${d.whopApiKeyEnv} is not set in the server environment `
                          + '— add it, then redeploy.',
                        shared: 'Blank, so this brand is using the shared WHOP_API_KEY. '
                          + 'That only works if this brand lives on the SAME Whop account; '
                          + 'a separate account needs its own variable name here.',
                        stored: `Blank, so this brand uses the key saved during onboarding (encrypted, ending …${e.storedWhopApiKeyLast4 || '????'}).`,
                        none: 'No key available — set one in the server environment.',
                      }[e.whopApiKeySource] || ''}
                      {e.whopWebhookSecretSource === 'stored'
                        ? ` Webhook secret: saved during onboarding (ending …${e.storedWebhookSecretLast4 || '????'}).`
                        : ''}
                    </small>
                  </label>
                  <label>
                    <span>Checkout URL</span>
                    <input
                      placeholder="https://whop.com/checkout/…"
                      value={d.checkoutUrl || ''}
                      onChange={(ev) => patch(e.slug, 'checkoutUrl', ev.target.value)}
                    />
                  </label>
                  <label>
                    <span>Trade Watch support emails</span>
                    <input
                      placeholder="support@brand.com, ops@brand.com"
                      value={d.tradeWatchSupportEmails || ''}
                      onChange={(ev) => patch(e.slug, 'tradeWatchSupportEmails', ev.target.value)}
                    />
                    <small className="ae-field-note">
                      Comma-separated. These get the naked-trade alert emails for this brand's
                      traders. Only admins see this list.
                    </small>
                  </label>
                </div>

                <BrandThemeEditor
                  theme={d.theme}
                  brandName={d.displayName || e.displayName}
                  logoUrl={d.logoUrl || e.branding?.logoUrl || ''}
                  onChange={(next) => patch(e.slug, 'theme', next)}
                />

                <BrandFeatureLocks
                  features={featureCatalog}
                  locked={d.lockedFeatures || []}
                  onChange={(next) => patch(e.slug, 'lockedFeatures', next)}
                />

                <BrandEmbedWidgetBuilder
                  entitySlug={e.slug}
                  displayName={d.displayName || e.displayName}
                  embedWidget={d.embedWidget || {}}
                  onChange={(next) => patch(e.slug, 'embedWidget', next)}
                />

                <BrandNt8WebhookTokens
                  entitySlug={e.slug}
                  displayName={d.displayName || e.displayName}
                  onNotice={setNotice}
                  onError={setError}
                />

                <button
                  type="button"
                  className="ae-btn ae-btn-primary"
                  onClick={() => save(e.slug)}
                  disabled={savingSlug === e.slug}
                >
                  {savingSlug === e.slug ? 'Saving…' : 'Save brand'}
                </button>
                  </div>
                ) : null}
              </section>
            );
          })}

          <section className="ae-card">
            <h2 className="ae-card-title">Add a brand</h2>
            <p className="ae-sub">
              Starts empty — all pages present, no strategies until you add or copy them.
            </p>
            <div className="ae-fields">
              <label>
                <span>Slug (permanent)</span>
                <input
                  placeholder="kairos"
                  value={newEntity.slug}
                  onChange={(ev) => setNewEntity((n) => ({ ...n, slug: ev.target.value }))}
                />
              </label>
              <label>
                <span>Display name</span>
                <input
                  placeholder="Kairos"
                  value={newEntity.displayName}
                  onChange={(ev) => setNewEntity((n) => ({ ...n, displayName: ev.target.value }))}
                />
              </label>
            </div>
            <button
              type="button"
              className="ae-btn ae-btn-primary"
              onClick={createEntity}
              disabled={creating || !newEntity.slug.trim() || !newEntity.displayName.trim()}
            >
              {creating ? 'Creating…' : 'Create brand'}
            </button>
          </section>

          <section className="ae-card">
            <h2 className="ae-card-title">Copy a strategy between brands</h2>
            <p className="ae-sub">
              Duplicates the strategy and its trades into the target brand. The copy is
              independent — editing one side never changes the other.
            </p>
            <div className="ae-fields">
              <label>
                <span>From</span>
                <select value={copy.from} onChange={(ev) => setCopy((c) => ({ ...c, from: ev.target.value }))}>
                  {entities.map((e) => <option key={e.slug} value={e.slug}>{e.displayName}</option>)}
                </select>
              </label>
              <label>
                <span>To</span>
                <select value={copy.to} onChange={(ev) => setCopy((c) => ({ ...c, to: ev.target.value }))}>
                  <option value="">Select…</option>
                  {entities.filter((e) => e.slug !== copy.from)
                    .map((e) => <option key={e.slug} value={e.slug}>{e.displayName}</option>)}
                </select>
              </label>
              <label>
                <span>Strategy name</span>
                <select
                  value={copy.strategyName}
                  onChange={(ev) => setCopy((c) => ({ ...c, strategyName: ev.target.value }))}
                  disabled={sourceStrategiesLoading || !sourceStrategies.length}
                >
                  <option value="">
                    {sourceStrategiesLoading && 'Loading…'}
                    {!sourceStrategiesLoading && sourceStrategiesError && "Couldn't load strategies"}
                    {!sourceStrategiesLoading && !sourceStrategiesError && (
                      sourceStrategies.length ? 'Select a strategy…' : 'No strategies in this brand'
                    )}
                  </option>
                  {/* Grouped so the two kinds are distinguishable: an equity
                      curve and a futures grid are different shapes of data. */}
                  {['equity', 'futures'].map((kind) => {
                    const group = sourceStrategies.filter((st) => (st.type || 'futures') === kind);
                    if (!group.length) return null;
                    return (
                      <optgroup
                        key={kind}
                        label={kind === 'equity' ? 'Equity & trade journals' : 'Futures strategies'}
                      >
                        {group.map((st) => {
                          // Show the display label (what Strategy Data calls it),
                          // and the raw DB name alongside when they differ — e.g.
                          // "Kairos" is stored as "Vector Capital Long Equity — QLD".
                          const label = st.label || st.name;
                          const differs = label !== st.name;
                          const size = st.tradeCount
                            ? `${fmtNum(st.tradeCount)} trades`
                            : st.pointCount
                              ? `${fmtNum(st.pointCount)} points`
                              : 'no data';
                          return (
                            <option key={`${kind}:${st.name}`} value={st.name}>
                              {`${label}${differs ? ` — ${st.name}` : ''} (${size})`}
                            </option>
                          );
                        })}
                      </optgroup>
                    );
                  })}
                </select>
                {sourceStrategiesError ? (
                  <small className="ae-field-note ae-field-error">{sourceStrategiesError}</small>
                ) : null}
              </label>
            </div>
            <button
              type="button"
              className="ae-btn ae-btn-primary"
              onClick={copyStrategy}
              disabled={!copy.to || !copy.strategyName.trim()}
            >
              Copy strategy
            </button>
          </section>

          <section className="ae-card">
            <h2 className="ae-card-title">Mirror Equity Portfolio from another brand</h2>
            <p className="ae-sub">
              Copies all portfolio leg strategies (AAPL, TSLA, NVDA, AMZN, PLTR), the full
              portfolio config (allocations, leverage, method), and the Equity Portfolio registry
              scenarios into the target brand — matching Vector&apos;s setup.
            </p>
            <div className="ae-fields">
              <label>
                <span>From</span>
                <select
                  value={portfolioCopy.from}
                  onChange={(ev) => setPortfolioCopy((c) => ({ ...c, from: ev.target.value }))}
                >
                  {entities.map((e) => <option key={e.slug} value={e.slug}>{e.displayName}</option>)}
                </select>
              </label>
              <label>
                <span>To</span>
                <select
                  value={portfolioCopy.to}
                  onChange={(ev) => setPortfolioCopy((c) => ({ ...c, to: ev.target.value }))}
                >
                  <option value="">Select…</option>
                  {entities.filter((e) => e.slug !== portfolioCopy.from)
                    .map((e) => <option key={e.slug} value={e.slug}>{e.displayName}</option>)}
                </select>
              </label>
              <label className="ae-checkbox-label">
                <input
                  type="checkbox"
                  checked={portfolioCopy.includeOg}
                  onChange={(ev) => setPortfolioCopy((c) => ({ ...c, includeOg: ev.target.checked }))}
                />
                <span>Include OG Equity Portfolios</span>
              </label>
              <label className="ae-checkbox-label">
                <input
                  type="checkbox"
                  checked={portfolioCopy.mirrorLive}
                  onChange={(ev) => setPortfolioCopy((c) => ({ ...c, mirrorLive: ev.target.checked }))}
                />
                <span>Live-mirror leg strategies from source (webhook feeds)</span>
              </label>
            </div>
            <button
              type="button"
              className="ae-btn ae-btn-primary"
              onClick={copyEquityPortfolio}
              disabled={!portfolioCopy.to || copyingPortfolio}
            >
              {copyingPortfolio ? 'Mirroring…' : 'Mirror Equity Portfolio'}
            </button>
          </section>
        </>
      )}
    </div>
  );
}
